import React, { useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { mockUsers } from '../data/mockData';
import { User } from '../types';
import '../styles/NewDocumentPage.css';

const NewDocumentPage: React.FC = () => {
  const { t } = useLanguage();
  const [tin, setTin] = useState('');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);

  const filteredUsers = mockUsers.filter(user => user.tin.startsWith(tin));

  const handleTinChange = (value: string) => {
    const numericValue = value.replace(/\D/g, '').slice(0, 14);
    setTin(numericValue);
    setShowDropdown(numericValue.length >= 10);
    if (numericValue.length < 10) {
      setSelectedUser(null);
    }
  };

  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
    setTin(user.tin);
    setShowDropdown(false);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
    }
  };

  const handleSubmit = async () => {
    if (!selectedUser || !file) return;

    setIsSubmitting(true);
    setLoadingProgress(0);

    // Плавное увеличение прогресса
    const duration = 4000;
    const steps = 100;
    const stepDuration = duration / steps;

    let currentProgress = 0;
    const interval = setInterval(() => {
      currentProgress += 1;
      if (currentProgress <= 100) {
        setLoadingProgress(currentProgress);
      } else {
        clearInterval(interval);
      }
    }, stepDuration);

    setTimeout(() => {
      clearInterval(interval);
      setIsSubmitting(false);
      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        setTin('');
        setSelectedUser(null);
        setFile(null);
        setLoadingProgress(0);
      }, 2000);
    }, duration);
  };

  const isFormValid = selectedUser && file;

  return (
    <div className="new-document-page">
      <div className="form-group">
        <label>{t('tin')}</label>
        <input
          type="text"
          value={tin}
          onChange={(e) => handleTinChange(e.target.value)}
          placeholder={t('tinPlaceholder')}
          className="tin-input"
        />
        {showDropdown && filteredUsers.length > 0 && (
          <div className="dropdown">
            {filteredUsers.map((user) => (
              <div
                key={user.tin}
                className="dropdown-item"
                onClick={() => handleUserSelect(user)}
              >
                {user.name}
              </div>
            ))}
          </div>
        )}
        {selectedUser && (
          <div className="selected-user">{selectedUser.name}</div>
        )}
      </div>

      <div className="form-group">
        <label>{t('documentUpload')}</label>
        <input
          type="file"
          id="file-upload"
          onChange={handleFileChange}
          className="file-input"
          accept=".pdf,.doc,.docx"
        />
        <label htmlFor="file-upload" className="file-label">
          {t('uploadFile')}
        </label>
        {file && (
          <div className="selected-file">{file.name}</div>
        )}
      </div>

      <button
        className={`submit-button ${isFormValid ? 'active' : ''}`}
        onClick={handleSubmit}
        disabled={!isFormValid}
      >
        {t('submit')}
      </button>

      <div className="sample-tins">
        <div className="sample-tins-title">Примеры ИНН для тестирования:</div>
        <div className="sample-tins-list">
          <span className="sample-tin">23322110099111</span>
          <span className="sample-tin">13322110099222</span>
          <span className="sample-tin">23322110099333</span>
          <span className="sample-tin">13322110099444</span>
        </div>
      </div>

      {isSubmitting && (
        <div className="modal-overlay">
          <div className="loader-container">
            <div className="loader"></div>
            <div className="loader-percentage">{loadingProgress}%</div>
            <div className="crypto-text">
              {loadingProgress < 30 && 'Генерация криптографического ключа...'}
              {loadingProgress >= 30 && loadingProgress < 60 && 'Создание цифровой подписи...'}
              {loadingProgress >= 60 && loadingProgress < 90 && 'Шифрование данных...'}
              {loadingProgress >= 90 && 'Финализация...'}
            </div>
          </div>
        </div>
      )}

      {showSuccess && (
        <div className="modal-overlay">
          <div className="success-check">
            <svg viewBox="0 0 52 52" className="checkmark">
              <circle className="checkmark-circle" cx="26" cy="26" r="25" fill="none" />
              <path className="checkmark-check" fill="none" d="M14.1 27.2l7.1 7.2 16.7-16.8" />
            </svg>
            <div className="success-text">{t('success')}</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewDocumentPage;
